import React, { createContext, useState, useEffect, useCallback, useRef } from 'react'
import { loginApi, signupApi, googleAuthApi, verifyOTP, refreshApi, logoutApi, AuthUser } from '../api/authApi'
import { setClientToken, setClientUserId } from '../api/client'

interface AuthContextType {
  user: AuthUser | null
  accessToken: string | null
  isLoading: boolean
  /** Resolves with the signed-in user, so a caller can act on the new identity
   *  without waiting for the context re-render (analytics needs the id at the
   *  moment of the event, not a tick later). */
  login: (email: string, password: string) => Promise<AuthUser>
  signup: (email: string, password: string, display_name: string) => Promise<AuthUser>
  /**
   * Resolves with `is_new` — true only when this sign-in created the account —
   * and the user, for the same reason as `login`. The caller needs `is_new` to
   * tell a registration apart from a returning login; nothing else about the
   * response is exposed.
   */
  loginWithGoogle: (credential: string) => Promise<{ is_new: boolean; user: AuthUser }>
  /**
   * SMS OTP sign-in. Same contract as `loginWithGoogle` — `is_new` is true only
   * when this verification created the account — plus the access token, which
   * the OTP form hands to its `onSuccess` caller.
   */
  loginWithOtp: (phone: string, code: string) =>
    Promise<{ is_new: boolean; user: AuthUser; accessToken: string }>
  logout: () => Promise<void>
  getToken: () => Promise<string | null>  // always returns valid token or null
}

export const AuthContext = createContext<AuthContextType | null>(null)

const ACCESS_TOKEN_TTL = 14 * 60 * 1000  // refresh 1 min before 15min expiry

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser]               = useState<AuthUser | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [isLoading, setIsLoading]     = useState(true)  // true until initial refresh attempt
  const refreshTimer                  = useRef<NodeJS.Timeout | undefined>(undefined)
  // Mirror of accessToken so getToken can read the latest value WITHOUT
  // depending on it — keeps getToken's identity stable across token refreshes,
  // so effects with [getToken] deps don't re-fire and duplicate API calls.
  const accessTokenRef                = useRef<string | null>(null)
  // True from the moment a login resolves. The mount restore below is for
  // reviving a session from the cookie, which is exactly what a fresh login has
  // already done — running both would be two refreshes racing each other.
  const justLoggedIn                  = useRef(false)

  /**
   * The ONLY way the access token is written.
   *
   * The ref is updated FIRST and synchronously. It used to be mirrored in an
   * effect, which left it a render behind: for that window getToken() saw null
   * while a perfectly good token sat in state, and called refreshApi() for a
   * token it already had. Two components doing that at once is what rotated the
   * refresh token twice and got the session revoked.
   */
  const applyAccessToken = useCallback((token: string | null) => {
    accessTokenRef.current = token
    setAccessToken(token)
  }, [])

  // Update API client token whenever accessToken changes
  useEffect(() => {
    setClientToken(accessToken)
  }, [accessToken])

  // Update API client user ID whenever user changes
  useEffect(() => {
    setClientUserId(user?.id || null)
  }, [user])

  // Schedule silent refresh before token expires
  const scheduleRefresh = useCallback((token: string) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current)
    refreshTimer.current = setTimeout(async () => {
      const result = await refreshApi()
      if (result) {
        applyAccessToken(result.accessToken)
        scheduleRefresh(result.accessToken)
      } else {
        // Refresh failed — session expired
        setUser(null)
        applyAccessToken(null)
      }
    }, ACCESS_TOKEN_TTL)
  }, [applyAccessToken])

  // On app load — try to restore session via refresh cookie
  useEffect(() => {
    async function restoreSession() {
      // A login that just happened has already established the session and its
      // tokens. Restoring on top of it would spend the refresh token a second
      // time, and the server reads a rotated token used twice as theft.
      if (justLoggedIn.current) {
        justLoggedIn.current = false
        setIsLoading(false)      // still has to release the app
        return
      }

      const result = await refreshApi()
      if (result) {
        applyAccessToken(result.accessToken)
        scheduleRefresh(result.accessToken)
        // Fetch user profile with new token
        try {
          const res = await fetch(`${process.env.REACT_APP_GATEWAY_URL || 'http://localhost:4000'}/api/profile/me`, {
            headers: { Authorization: `Bearer ${result.accessToken}` }
          })
          if (res.ok) {
            const profile = await res.json()
            setUser({
              id:           profile.id,
              // null for a phone-only account — /profile/me strips the
              // placeholder address before it ever reaches the client.
              email:        profile.email ?? null,
              phone:        profile.phone ?? null,
              display_name: profile.display_name,
              is_verified:  profile.is_verified,
              role:         'customer'
            })
          }
        } catch { /* silent */ }
      }
      setIsLoading(false)
    }
    restoreSession()
    return () => { if (refreshTimer.current) clearTimeout(refreshTimer.current) }
  }, [scheduleRefresh, applyAccessToken])

  const login = useCallback(async (email: string, password: string) => {
    const data = await loginApi(email, password)
    justLoggedIn.current = true
    applyAccessToken(data.accessToken)
    setUser(data.user)
    scheduleRefresh(data.accessToken)
    return data.user
  }, [scheduleRefresh, applyAccessToken])

  const signup = useCallback(async (email: string, password: string, display_name: string) => {
    const data = await signupApi(email, password, display_name)
    justLoggedIn.current = true
    applyAccessToken(data.accessToken)
    setUser(data.user)
    scheduleRefresh(data.accessToken)
    return data.user
  }, [scheduleRefresh, applyAccessToken])

  // Same session-establishment path as `login`, but authenticated by a Google
  // ID token instead of email+password.
  const loginWithGoogle = useCallback(async (credential: string) => {
    const data = await googleAuthApi(credential)
    justLoggedIn.current = true
    applyAccessToken(data.accessToken)
    setUser(data.user)
    scheduleRefresh(data.accessToken)
    // Handed straight back rather than stored: it describes this one request,
    // not the session, and would be wrong the moment the session is restored
    // from the refresh cookie.
    return { is_new: data.is_new, user: data.user }
  }, [scheduleRefresh, applyAccessToken])

  // Same session-establishment path as `login`, authenticated by a one-time SMS
  // code instead of email+password. The token/cookie handling on the server is
  // identical, so nothing here needs to differ.
  const loginWithOtp = useCallback(async (phone: string, code: string) => {
    const data = await verifyOTP(phone, code)
    justLoggedIn.current = true
    applyAccessToken(data.accessToken)
    setUser(data.user)
    scheduleRefresh(data.accessToken)
    // `is_new` describes this one request, not the session — handed back rather
    // than stored, for the same reason as loginWithGoogle.
    return { is_new: data.is_new, user: data.user, accessToken: data.accessToken }
  }, [scheduleRefresh, applyAccessToken])

  const logout = useCallback(async () => {
    await logoutApi()
    setUser(null)
    applyAccessToken(null)
    if (refreshTimer.current) clearTimeout(refreshTimer.current)
  }, [applyAccessToken])

  // Use this in API calls — always returns a valid token. Reads the token via
  // ref so its identity never changes (stable across the whole session).
  const getToken = useCallback(async (): Promise<string | null> => {
    if (accessTokenRef.current) return accessTokenRef.current
    const result = await refreshApi()
    if (result) {
      applyAccessToken(result.accessToken)
      scheduleRefresh(result.accessToken)
      return result.accessToken
    }
    return null
  }, [scheduleRefresh, applyAccessToken])

  return (
    <AuthContext.Provider value={{ user, accessToken, isLoading, login, signup, loginWithGoogle, loginWithOtp, logout, getToken }}>
      {children}
    </AuthContext.Provider>
  )
}
